/**
 * File utility class for handling file operations
 * Provides methods for file conversion, validation, and processing
 */

import { getLogger } from "@/services/shared/LoggerFactory";

export interface FileConversionResult {
  data: string;
  filename: string;
  mime_type: string;
}

export class FileUtils {
  /**
   * Convert File to base64 format
   * @param file - The file to convert
   * @returns Promise resolving to base64 data with metadata
   */
  static async convertToBase64(file: File): Promise<FileConversionResult> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64Data = result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
        resolve({
          data: base64Data,
          filename: file.name,
          mime_type: file.type
        });
      };
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * Convert File to base64 string only (without metadata)
   * @param file - The file to convert
   * @returns Promise resolving to base64 string
   */
  static async convertToBase64String(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64Data = result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
        resolve(base64Data);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * Safely convert file to base64 with error handling
   * @param file - The file to convert
   * @returns Promise resolving to base64 data or undefined if conversion fails
   */
  static async safeConvertToBase64(file: File): Promise<FileConversionResult | undefined> {
    try {
      return await this.convertToBase64(file);
    } catch (error) {
      const logger = getLogger();
      logger.warn('Photo conversion failed:', error);
      return undefined;
    }
  }

  /**
   * Safely convert file to base64 string with error handling
   * @param file - The file to convert
   * @returns Promise resolving to base64 string or undefined if conversion fails
   */
  static async safeConvertToBase64String(file: File): Promise<string | undefined> {
    try {
      return await this.convertToBase64String(file);
    } catch (error) {
      const logger = getLogger();
      logger.warn('File conversion failed:', error);
      return undefined;
    }
  }

  /**
   * Validate file type and size
   * @param file - The file to validate
   * @param allowedTypes - Array of allowed MIME types
   * @param maxSizeBytes - Maximum file size in bytes
   * @returns Validation result
   */
  static validateFile(
    file: File,
    allowedTypes: string[] = ['image/jpeg', 'image/png', 'image/gif'],
    maxSizeBytes: number = 5 * 1024 * 1024 // 5MB default
  ): { isValid: boolean; error?: string } {
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `File type ${file.type} is not allowed. Allowed types: ${allowedTypes.join(', ')}`
      };
    }

    if (file.size > maxSizeBytes) {
      return {
        isValid: false,
        error: `File size ${file.size} bytes exceeds maximum allowed size of ${maxSizeBytes} bytes`
      };
    }

    return { isValid: true };
  }

  /**
   * Format file size in human-readable format
   * @param bytes - File size in bytes
   * @returns Formatted file size string
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

// Legacy function for backward compatibility
export const fileToBase64 = FileUtils.convertToBase64;